import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';

import { MESSAGES } from 'src/constants/messages';
import { useAssignedRiskApplicationAgentDetails } from 'src/api/hooks/useAssignedRiskApplicationQueries';

import { formatPhoneNumber } from '../../../../utils/format-phone';

function display(value: string | number | boolean | null | undefined): string {
  return value === null || value === undefined || value === '' ? '' : String(value);
}

const agentMessages = MESSAGES.ASSIGNED_RISK_APPLICATION.AGENT_DETAILS;

export default function AgentDetails({ assignedRiskAppId }: Readonly<{ assignedRiskAppId: number }>) {
  const { data, isLoading, isError } = useAssignedRiskApplicationAgentDetails(assignedRiskAppId);
  if (isLoading) {
    return (
      <Card sx={{ mt: 3 }} data-testid="assigned-risk-agent-details-loading">
        <CardContent sx={{ p: 3 }}>
          <Skeleton variant="text" width={160} height={32} />
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {Array.from({ length: 6 }, (_, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="text" width="80%" />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  }
  if (isError) return <Alert severity="error" data-testid="assigned-risk-agent-details-error">{agentMessages.LOAD_ERROR}</Alert>;

  const agent = data?.data;
  const address = [agent?.street, agent?.city, agent?.stateAbbreviation, agent?.zipCode]
    .filter(Boolean)
    .join(', ');

  const fields = [
    [agentMessages.FIELD_LABELS.NAME, display(agent?.agentName)],
    [agentMessages.FIELD_LABELS.EMAIL, display(agent?.email)],
    [agentMessages.FIELD_LABELS.AGENCY, display(agent?.agency)],
    [agentMessages.FIELD_LABELS.ADDRESS, display(address)],
    [agentMessages.FIELD_LABELS.PHONE, agent?.phoneNumber ? formatPhoneNumber(agent.phoneNumber) : ''],
    [agentMessages.FIELD_LABELS.FEIN, display(agent?.feinLastFive)],
  ];

  return (
    <Card sx={{ mt: 3 }} data-testid="assigned-risk-agent-details-card">
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h5">{agentMessages.TITLE}</Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {fields.map(([label, value]) => (
            <Grid key={label} size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {label}
              </Typography>
              <Typography
                variant="subtitle2"
                data-testid={`agent-${label.toLowerCase().replaceAll(' ', '-')}`}
              >
                {value}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
}